/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';

import { Delete } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridColumnVisibilityModel, GridExpandMoreIcon } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import { promoCodePath, promoIdPath } from '~/components/Promo/promoUrls';
import { mapPromoLogV2RowsToPromoLogs } from '~/components/PromoV3/mapPromoLogV2RowsToPromoLogs';
import { parsePromoRequestItemsFromPromoRequest } from '~/components/Purchases/utils/parsePromoRequestItemsFromPromoRequest';

import { DEFAULT_PAGE_SIZES } from '~/consts/filters';

import useCurrentTenant from '~/hooks/useCurrentTenant';
import useQuery from '~/hooks/useQuery';

import { addDays, formatDateLong, formatDateLongMonthWithMeridiem, formatDateShort } from '~/services/TimeService';

import currencyFormatter from '~/utils/currencyFormatter';

import {
  DiscountItemInfo,
  InternalPromoWithDiscounts,
  PromoLog,
  PromoTypesAndUnknown,
  getFilterTitle,
  getLogsByPromo,
} from '../../services/PromoService';
import CopyableField from '../Common/CopyableField';
import PageSubheader from '../Common/Elements/PageSubheader';
import ErrorDisplay from '../Common/ErrorDisplay';
import DebugModal from '../DebugModal/DebugModal';
import PromoRequestItems from '../Promo/PromoRequests/PromoRequestItems';
import ColumnSelector from '../Promo/PromoTester/ColumnSelector';
import PromoTesterLink from '../Promo/PromoTester/PromoTesterLink';
import { PromoCodeName } from '../Promo/formatters/PromoCodeNameFormatter';
import PromoLogResultsFormatter from '../Promo/formatters/promoLogResultsFormatter';
import { PromoRequestItem } from '../Promo/hooks/usePromoRequests';

import { PromoItemResultTableCell } from './PromoItemResultTableCell';
import UserAndOrderId from './UserAndOrderId';

const sharedAttrs: Pick<GridColDef, 'sortable' | 'display' | 'flex'> = {
  sortable: false,
  display: 'flex',
  flex: 0,
};

type GetColumnsProps = {
  meta: definitions['PromoMeta'];
};

function legacyPromoLogDetails(details: unknown): { discountItems: Array<DiscountItemInfo>; region: string } {
  if (!details || typeof details !== 'object') {
    return { discountItems: [], region: '' };
  }
  const o = details as Record<string, unknown>;
  const discountItems = Array.isArray(o.discountItems) ? (o.discountItems as Array<DiscountItemInfo>) : [];
  const lr = o.logResults as Record<string, unknown> | undefined;
  const inputOrder = lr?.inputOrder as Record<string, unknown> | undefined;
  const regionFromRoot = typeof o.region === 'string' ? o.region : '';
  const regionFromOrder = typeof inputOrder?.region === 'string' ? (inputOrder.region as string) : '';
  return { discountItems, region: regionFromRoot || regionFromOrder };
}

const getColumns = ({ meta }: GetColumnsProps): Array<GridColDef> => [
  {
    field: 'created_at',
    valueFormatter: (value) => formatDateShort(value),
    renderCell: (params) => (
      <Box title={formatDateLongMonthWithMeridiem(params.row.created_at)}>{formatDateShort(params.row.created_at)}</Box>
    ),
    headerName: 'Logged at',
    ...sharedAttrs,
    flex: 0,
  },
  {
    field: 'code_name',
    renderCell: (params) => PromoCodeName({ codeName: params.row.code_name }),
    headerName: 'Code',
    ...sharedAttrs,
  },
  {
    field: 'deleted',
    renderCell: (params) =>
      params.row.deleted ? (
        <Typography title={`log deleted at ${formatDateLong(params.row.deleted)} [${params.row.deleted}]`}>
          <Delete />
          deleted:{formatDateShort(params.row.deleted)}
        </Typography>
      ) : (
        <>---</>
      ),
    headerName: 'Deleted',
    ...sharedAttrs,
  },
  {
    field: 'amount',
    valueFormatter: (value) => formatDateShort(value),
    renderCell: (params) => (
      <Stack>
        <Box component="span" color="success.main">
          {currencyFormatter(params.row.currency ?? 'AUD', params.row.amount)}
        </Box>
      </Stack>
    ),
    headerName: 'Amount',
    ...sharedAttrs,
  },
  {
    field: 'orderAndUser',
    headerName: 'User/Order',
    renderCell: (params) => UserAndOrderId({ orderId: params.row.fk_orders, userId: params.row.used_by }),
    ...sharedAttrs,
    flex: 2,
  },
  {
    field: 'validation',
    headerName: 'Amount Validation',
    renderCell: (params) => PromoLogResultsFormatter(params.row),
    ...sharedAttrs,
  },
  {
    field: 'details',
    headerName: 'Details',
    description: 'Details of the promo applying to the order - PromoDiscount, ItemPricing, categoryBK, productBK etc',
    sortable: false,
    renderCell: (params) => {
      const { discountItems, region } = legacyPromoLogDetails(params.row.details);

      if (!params.row.details) {
        return (
          <Typography title="No details? This is unexpected for recent promos and can represent a problem">
            No Log details
          </Typography>
        );
      }

      if (discountItems.length === 0) {
        return (
          <Typography title="No discount items on promo_log. This is unexpected for recent promos and can represent a problem">
            No Log Discount Items
          </Typography>
        );
      }
      // When the promo_code_name (the code_name of the promo we're viewing), doesn't match the code_name on the log, its a child usage
      const showChildPromoLink = params.row.parent_id_promo_code != null;

      return (
        <Stack direction="column" width="90%">
          <PromoItemResultTableCell
            discountItems={discountItems}
            currency={params.row.currency}
            region={region}
            meta={meta}
          />
          {region}
          <div>
            {showChildPromoLink && params.row.promo_code_name === 'ALL' && (
              <Link target="_blank" href={promoIdPath(params.row.parent_id_promo_code)}>
                [child of {params.row.parent_id_promo_code}]
              </Link>
            )}
            {showChildPromoLink && params.row.promo_code_name !== 'ALL' && (
              <Link target="_blank" href={promoCodePath(params.row.promo_code_name)}>
                [child of {params.row.promo_code_name}]
              </Link>
            )}
          </div>
        </Stack>
      );
    },
    ...sharedAttrs,
    flex: 4,
  },
  {
    field: 'debug',
    headerName: '',
    renderCell: (params) => (
      <Box>
        <CopyableField
          value={JSON.stringify(params.row, null, 4)}
          label={
            <DebugModal
              title={`v${params.row.details?.logResults?.inputOrder?.clientOrderVersion || 0}`}
              type="generic"
              data={params.row ?? { data: 'None' }}
            />
          }
        />
      </Box>
    ),
    ...sharedAttrs,
  },
  {
    field: 'reSimulate',
    headerName: '',
    renderCell: (params) =>
      PromoTesterLink({
        codeName: params.row.code_name,
        orderId: params.row.fk_orders,
      }),
    ...sharedAttrs,
    flex: 0,
  },
];

interface PromoLogs {
  [index: number]: PromoLog;
}

interface Props {
  promo?:
    | {
        code_name: string;
        promo_type: PromoTypesAndUnknown;
      }
    | InternalPromoWithDiscounts;
  userIdFilter?: string;
  meta: definitions['PromoMeta'];
  defaultExpanded?: boolean;
  initPage?: number;
  initLimit?: number;
  onPageChange?: (page: number, limit: number) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  initLogAfter?: Date;
  initLogBefore?: Date;
  includeDeleted?: boolean;
  onDateFiltersChange?: (logAfter?: string, logBefore?: string) => void;
  onIncludeDeletedChange?: (includeDeleted: boolean) => void;
  updateUrlState?: (filter: {
    userId?: string;
    expanded?: boolean;
    itemsExpanded?: boolean;
    logsExpanded?: boolean;
    failuresExpanded?: boolean;
    usersExpanded?: boolean;
  }) => void;
  defaultVisibleItemFields?: Array<string>;
}

function PromoLogsContainer({
  promo,
  userIdFilter,
  meta,
  defaultExpanded = false,
  initPage = 0,
  initLimit = 10,
  onPageChange: externalOnPageChange,
  expanded,
  onExpandedChange,
  initLogAfter,
  initLogBefore,
  includeDeleted,
  onDateFiltersChange,
  onIncludeDeletedChange,
  updateUrlState,
  defaultVisibleItemFields = [],
}: Props) {
  const [isLogsLoading, setIsLogsLoading] = useState(true);
  const [hasErrorToFetchLogs, setHasErrorToFetchLogs] = useState('');
  const [promoLogs, setPromoLogs] = useState<Array<PromoLogs>>();
  const [promoLogRequestItems, setPromoLogRequestItems] = useState<Array<PromoRequestItem>>([]);
  const [total, setTotal] = useState<number>(0);
  const query = useQuery();

  const [page, setPage] = useState<number>(initPage);
  const [limit, setLimit] = useState<number>(initLimit);
  const [logBefore, setLogBefore] = useState<string | undefined>(initLogBefore?.toISOString().split('T')[0]);
  const [logAfter, setLogAfter] = useState<string | undefined>(initLogAfter?.toISOString().split('T')[0]);
  const [includeDeletedForm, setIncludeDeletedForm] = useState<boolean>(includeDeleted || false);
  const isDev = !!query.get('dev');
  const [allTime, setAllTime] = useState<boolean>(false);

  // Column visibility state for PromoLogs
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    deleted: false,
  });

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: visible,
    }));
  };

  const {
    tenant: { brand },
  } = useCurrentTenant();
  const promoBrand: definitions['Promo Brands'] =
    brand === 'newwhitelabel' || brand === 'yidu' ? 'luxuryescapes' : brand;

  const onPageChange = (page: number, limit: number) => {
    setPage(page);
    setLimit(limit);
    if (externalOnPageChange) {
      externalOnPageChange(page, limit);
    }
  };

  const fetchLogsData = async () => {
    if (!promo) {
      return;
    }
    setIsLogsLoading(true);
    try {
      const logResponse = await getLogsByPromo({
        codeName: promo.code_name,
        brand: promoBrand,
        userId: userIdFilter,
        includeDeleted: includeDeletedForm,
        page,
        limit,
        logBefore: logBefore ? new Date(logBefore) : undefined,
        logAfter: logAfter ? new Date(logAfter) : undefined,
      });
      const {
        logs = [],
        log_items_2: logItems2 = [],
        total: logTotal,
      } = (logResponse.result ?? {}) as NonNullable<typeof logResponse.result> & {
        log_items_2?: Parameters<typeof parsePromoRequestItemsFromPromoRequest>[0];
      };
      setPromoLogs(mapPromoLogV2RowsToPromoLogs(logs, promo));
      setPromoLogRequestItems(parsePromoRequestItemsFromPromoRequest(logItems2));
      setTotal(logTotal ?? logResponse.total ?? 0);
      setHasErrorToFetchLogs('');
    } catch (err) {
      if (err instanceof Error) {
        setHasErrorToFetchLogs(`Error fetching logs: ${JSON.stringify({ err: err.message, stack: err.stack })}`);
      } else {
        setHasErrorToFetchLogs('Error fetching logs: An unknown error occurred');
      }
    } finally {
      setIsLogsLoading(false);
    }
  };

  // Immediate effect for non-form changes
  useEffect(() => {
    fetchLogsData();
  }, [promoBrand, promo, userIdFilter, limit, page, isDev, includeDeleted]);

  // Debounced effect for form changes only
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchLogsData();
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [logBefore, logAfter, includeDeletedForm, allTime]);

  const handleSetTime = (allTime: boolean) => {
    if (allTime) {
      setLogAfter(undefined);
      setLogBefore(undefined);
      setAllTime(true);
    } else {
      setLogAfter(addDays(-365, new Date()).toISOString().split('T')[0]);
      setLogBefore(addDays(365, new Date()).toISOString().split('T')[0]);
      setAllTime(false);
    }
  };

  const handleIncludeDeletedChange = (checked: boolean) => {
    setIncludeDeletedForm(checked);
    onIncludeDeletedChange?.(checked);
  };

  return (
    <>
      <Accordion disableGutters expanded={expanded} onChange={(_, expanded) => onExpandedChange?.(expanded)}>
        <AccordionSummary
          expandIcon={<GridExpandMoreIcon />}
          aria-controls="offer-fields"
          id="offer-fields"
          sx={{
            backgroundColor: 'action.hover',
            color: 'text.primary',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <PageSubheader
            title={getFilterTitle({
              prefix:
                promo && 'usage_count' in promo
                  ? `Promo Logs ${promo.usage_count}${promo?.code_limit ? `/${promo?.code_limit}` : ''} uses`
                  : `Promo Logs (${total})`,
              codeName: promo?.code_name,
              userId: userIdFilter,
              includeDeleted: includeDeleted,
            })}
          />
        </AccordionSummary>
        <AccordionDetails>
          {hasErrorToFetchLogs && <ErrorDisplay message={hasErrorToFetchLogs} />}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControlLabel
              control={<Checkbox checked={allTime} onChange={(e) => handleSetTime(e.target.checked)} />}
              label="All time"
            />
            <TextField
              label="Log After"
              type="date"
              size="small"
              value={logAfter || ''}
              onChange={(e) => setLogAfter(e.target.value || undefined)}
              defaultValue={initLogAfter?.toISOString().split('T')[0]}
            />
            <TextField
              label="Log Before"
              type="date"
              size="small"
              value={logBefore || ''}
              onChange={(e) => setLogBefore(e.target.value || undefined)}
            />
            <FormControlLabel
              control={
                <Checkbox checked={includeDeletedForm} onChange={(e) => handleIncludeDeletedChange(e.target.checked)} />
              }
              label="Include Deleted"
            />
          </Box>

          {!isLogsLoading && (
            <Box sx={{ position: 'relative' }}>
              <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                <ColumnSelector
                  columns={getColumns({ meta: meta })}
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityChange={handleColumnVisibilityChange}
                />
              </Box>
              <DataGrid
                columns={getColumns({ meta: meta })}
                autoHeight
                getRowId={(row) => row.fk_orders}
                rowCount={total}
                rows={promoLogs || []}
                pageSizeOptions={DEFAULT_PAGE_SIZES}
                onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
                initialState={{ pagination: { paginationModel: { pageSize: limit, page } } }}
                paginationModel={{ page, pageSize: limit }}
                getRowHeight={() => 'auto'}
                paginationMode="server"
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
              />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
      <PromoRequestItems
        meta={meta}
        promoRequestItems={promoLogRequestItems}
        highlightedRows={[]}
        requestType="Log Related"
        isLoading={isLogsLoading}
        updateUrlState={updateUrlState}
        defaultVisibleItemFields={defaultVisibleItemFields}
      />
    </>
  );
}

export default PromoLogsContainer;
